import React from 'react';
import { createPortal } from 'react-dom';
import { X, CalendarClock, Inbox } from 'lucide-react';
import type { EmissaoFaturamento } from '../../../../src/lib/financeiro/metricasFaturamento';
import { diasDeAtrasoDaEmissao } from '../../../../src/lib/financeiro/metricasFaturamento';
import { limiteEmissaoNoPrazo } from '../../../../src/lib/janelaFaturamento';
import { formatDateString } from '../../../../src/lib/janelaCobranca';
import { propsFundoModal } from '../../../../src/lib/backdropModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  emissoes: EmissaoFaturamento[];
  rotuloPeriodo: string;
}

/** 'YYYY-MM-DD' do Postgres em dd/MM/yyyy, sem deixar o fuso recuar um dia. */
const dataCurta = (iso?: string | null) =>
  iso ? formatDateString(new Date(`${String(iso).slice(0, 10)}T12:00:00`)) : '—';

const dataHora = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Quais faturas estão por trás do número "fora do prazo".
 *
 * O card sozinho diz quantas; sem a lista não há como agir — é aqui que se
 * descobre QUAL UC atrasou, de quanto foi e o que o responsável registrou.
 * Respeita os mesmos recortes da seção (período, pessoa, time).
 */
const FaturasForaDoPrazoModal: React.FC<Props> = ({ isOpen, onClose, emissoes, rotuloPeriodo }) => {
  const tituloId = React.useId();

  // ESC fecha: esta é uma tela de leitura, não um gate de confirmação — aqui
  // sair rápido é o comportamento esperado, ao contrário dos modais de emissão.
  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
      {...propsFundoModal(() => onClose())}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
              <CalendarClock size={20} />
            </div>
            <div className="min-w-0">
              <h3 id={tituloId} className="text-lg font-bold text-gray-900 dark:text-white">
                Boletos emitidos fora do prazo
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {emissoes.length === 1 ? '1 emissão' : `${emissoes.length} emissões`} · {rotuloPeriodo}
                {' '}· da mais atrasada para a menos
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4">
          {emissoes.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <Inbox size={24} className="text-gray-300 dark:text-gray-600" />
              <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                Nenhum boleto fora do prazo neste período
              </p>
            </div>
          ) : (
            <table className="w-full min-w-[860px] text-sm">
              <thead className="sticky top-0 bg-white dark:bg-gray-800">
                <tr className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 px-2 text-left font-semibold">UC / Cliente</th>
                  <th className="py-2 px-2 text-left font-semibold">Mês ref.</th>
                  <th className="py-2 px-2 text-center font-semibold">Vencimento</th>
                  <th className="py-2 px-2 text-center font-semibold">Prazo até</th>
                  <th className="py-2 px-2 text-center font-semibold">Emitido em</th>
                  <th className="py-2 px-2 text-center font-semibold">Atraso</th>
                  <th className="py-2 px-2 text-left font-semibold">Responsável</th>
                  <th className="py-2 px-2 text-left font-semibold">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {emissoes.map((e, i) => {
                  const limite = limiteEmissaoNoPrazo(e.dataVencimento);
                  const atraso = diasDeAtrasoDaEmissao(e);
                  return (
                    <tr
                      key={`${e.faturaId}-${i}`}
                      className="border-b border-gray-50 dark:border-gray-700/50 align-top hover:bg-gray-50 dark:hover:bg-gray-700/30"
                    >
                      <td className="py-2.5 px-2">
                        <span className="block font-mono text-[12px] text-gray-700 dark:text-gray-200">{e.ucCodigo || '—'}</span>
                        <span className="block text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[210px]" title={e.clienteNome || ''}>
                          {e.clienteNome || 'Sem cliente'}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{e.mesReferencia || '—'}</td>
                      <td className="py-2.5 px-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{dataCurta(e.dataVencimento)}</td>
                      <td className="py-2.5 px-2 text-center text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {limite ? formatDateString(limite) : '—'}
                      </td>
                      <td className="py-2.5 px-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{dataHora(e.emitidoEm)}</td>
                      <td className="py-2.5 px-2 text-center whitespace-nowrap">
                        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                          {atraso === 1 ? '1 dia' : `${atraso} dias`}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-gray-700 dark:text-gray-300 truncate max-w-[150px]" title={e.emitidoPorNome}>
                        {e.emitidoPorNome}
                      </td>
                      <td className="py-2.5 px-2 max-w-[220px]">
                        {e.motivo ? (
                          <>
                            <span className="block text-[12px] text-gray-700 dark:text-gray-200">{e.motivo}</span>
                            {e.justificativa && (
                              <span className="block text-[11px] text-gray-500 dark:text-gray-400 break-words">{e.justificativa}</span>
                            )}
                          </>
                        ) : (
                          // Emissões anteriores ao registro de motivo. Dizer o
                          // porquê evita que a coluna vazia pareça um bug.
                          <span className="text-[11px] italic text-gray-400 dark:text-gray-500">
                            sem motivo registrado
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FaturasForaDoPrazoModal;
